/**
 * EMAIL LIST EDITOR — Gestione inline della lista email di un'entita'.
 *
 * Riusabile da PersonDetail e BusinessDetail (entita' polimorfiche Liferay).
 * Ogni email e' una riga con:
 *   - EditableField text per l'indirizzo (con validazione regex)
 *   - Switch per primary flag
 *   - IconButton per rimuovere
 *
 * In fondo un pulsante "+ Aggiungi" che apre un input inline vuoto.
 * Blur con valore vuoto -> chiude senza creare nulla ("add on confirm").
 *
 * La logica handle-update / auto-promote / toast e' delegata a
 * `useSimpleListItemEditor`, pattern condiviso con PhoneListEditor.
 */

import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useAppDispatch } from '../store/hooks';
import { useCurrentGroupId } from '../auth/authSelectors';
import { ConfirmDialog } from './ConfirmDialog';
import { useConfirmDialog } from './useConfirmDialog';
import { EditableField } from './EditableField';
import { NewSimpleListItemInput } from './NewSimpleListItemInput';
import { useSimpleListItemEditor } from './useSimpleListItemEditor';
import type { EmailAddress, EmailAddressOwnerClassName } from '../entities/emailAddress/emailAddressTypes';
import { addEmailAddress, updateEmailAddress, removeEmailAddress } from '../entities/emailAddress/emailAddressThunks';
import { personApi } from '../entities/person/personApi';

interface EmailListEditorProps {
  className: EmailAddressOwnerClassName;
  classPK: number;
  emailAddresses: EmailAddress[];
}

const EMAIL_REGEX = /^.+@.+\..+$/;

export function EmailListEditor({ className, classPK, emailAddresses }: EmailListEditorProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const groupId = useCurrentGroupId();
  // FILO-18: il cestino chiede conferma, la cancellazione non si può disfare.
  const confirm = useConfirmDialog();

  // Univocità email bloccante, coerente con la creazione (PersonFormBody): prima
  // di add/update verifichiamo che l'indirizzo non appartenga a un ALTRO
  // contatto. Solo per le Person (il Business non ha questo vincolo) e
  // escludendo il contatto stesso (classPK). Lancia -> il hook mostra il toast
  // di errore (e serverError inline su update); best-effort: se il lookup
  // fallisce non blocchiamo.
  async function assertEmailUnique(address: string) {
    if (className !== 'Person') return;
    const trimmed = address.trim();
    if (!trimmed.includes('@')) return;
    // .catch -> null: best-effort, se il lookup fallisce non blocchiamo.
    const existing = await personApi.getByEmailAddress(groupId, trimmed).catch(() => null);
    if (existing && existing.personId !== classPK) {
      throw new Error(t('person.duplicateEmail.fieldError'));
    }
  }

  const {
    isAdding,
    setIsAdding,
    validateValue,
    handleUpdateValue,
    handleSetPrimary,
    handleRemove,
    handleAdd,
  } = useSimpleListItemEditor<EmailAddress>({
    items: emailAddresses,
    getId: (e) => e.emailAddressId,
    getPrimary: (e) => e.primary,
    getValue: (e) => e.address,
    onAdd: async (address, primary) => {
      await assertEmailUnique(address);
      return dispatch(addEmailAddress({ className, classPK, address, primary })).unwrap();
    },
    onUpdate: async (e, address, primary) => {
      // Check solo se l'indirizzo cambia davvero: set-primary/auto-promote
      // riusano lo stesso address e non vanno riverificati (sarebbe self-match).
      if (address !== e.address) await assertEmailUnique(address);
      return dispatch(updateEmailAddress({ emailAddressId: e.emailAddressId, className, classPK, address, primary })).unwrap();
    },
    onRemove: (e) =>
      dispatch(removeEmailAddress({ emailAddressId: e.emailAddressId, className, classPK })).unwrap(),
    validate: (str) => (EMAIL_REGEX.test(str) ? null : t('editableField.emailInvalid')),
  });

  return (
    <div className="list-editor">
      <div className="list-editor__label">{t('common.email')}</div>
      {emailAddresses.map((email) => (
        <div key={email.emailAddressId} className={`list-editor__row le-item${email.primary ? ' is-primary' : ''}`}>
          {/* Stella a sinistra = "primaria": piena se selezionata. Click su una
              non-primaria la imposta come primaria (la primaria resta disabilitata,
              c'è sempre una sola primaria). */}
          <IconButton
            className="le-star"
            size="small"
            onClick={() => handleSetPrimary(email)}
            disabled={email.primary}
            aria-label={email.primary ? t('editableField.primary') : t('editableField.setAsPrimary')}
            title={email.primary ? t('editableField.primary') : t('editableField.setAsPrimary')}
          >
            {email.primary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
          <div className="list-editor__row-value">
            <EditableField
              value={email.address}
              variant={{ kind: 'text' }}
              validate={validateValue}
              required
              onCommit={(v) => handleUpdateValue(email, v)}
              label={t('common.email')}
            />
          </div>
          <IconButton
            className="le-remove"
            size="small"
            onClick={() => confirm.requestConfirm(
              t('editableField.removeEmailConfirm', { value: email.address }),
              () => handleRemove(email),
            )}
            aria-label={t('common.remove')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ))}
      {isAdding ? (
        <NewSimpleListItemInput
          onConfirm={handleAdd}
          onCancel={() => setIsAdding(false)}
          placeholder="nuova@email.it"
        />
      ) : (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setIsAdding(true)}
          className="btn-lowercase mt-xs"
        >
          {t('common.add')}
        </Button>
      )}
      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        {...confirm.options}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
      />
    </div>
  );
}
